import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import Button from '~/components/Button'
import usePolicies from '@verzekeringapp-mono/shared/src/hooks/use_policies'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { addYears, format } from 'date-fns'
import { getPolicyById } from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import { useNavigate } from 'react-router-dom'
import dateUtils from '@verzekeringapp-mono/shared/src/utils/date_utils'
import useValueMeterForms from '@verzekeringapp-mono/shared/src/hooks/use_value_meter_forms'
import { getHouseHoldValueMeterForm as getHouseHoldValueMeterFormSelector } from '@verzekeringapp-mono/shared/src/state/selectors/value_meter_forms'
import { ValueMeterFormQuestions } from './components/ValueMeterFormQuestions'
import { getPendingRelationMutations } from '@verzekeringapp-mono/shared/src/state/selectors/relation_mutations'
import { buildFormSubmissionPayload } from '@verzekeringapp-mono/shared/src/utils/form_builder_utils'

const HouseHoldEffectsForm = () => {
  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  const {
    formState: { isValid }
  } = form

  const { postContentsInsuranceValue } = usePolicies()
  const { id } = useParams()
  const valueMeterIsLoading = useAppSelector(
    state => state.policies.isLoadingValueMeter
  )
  const idNotNull = id ? id : ''
  const policy = useAppSelector(getPolicyById(idNotNull))
  const navigate = useNavigate()
  const { getHouseHoldValueMeterForm } = useValueMeterForms()
  const pendingRelationMutations = useAppSelector(getPendingRelationMutations)
  const valueMeterIsPending = useMemo(() => pendingRelationMutations?.activePolicyChanges.includes(policy ? Number(policy.policyNumber) : -1), [pendingRelationMutations, policy])

  const questions = useAppSelector(getHouseHoldValueMeterFormSelector)

  useEffect(() => {
    getHouseHoldValueMeterForm()
  }, [])

  const underInsuranceGuaranteeDate =
    policy?.details?.find(
      detail => detail.name === 'underinsurance_guarantee_date'
    )?.code || null

  const expireDate = underInsuranceGuaranteeDate
    ? addYears(dateUtils.dutchDateToJSDate(underInsuranceGuaranteeDate), 5)
    : null

  const showConfirmPostValueMeter = useCallback(() => {
    const confirm = window.confirm(
      'De ingevulde waardemeter wordt zo spoedig mogelijk door je adviseur in behandeling genomen.'
    )

    if (!confirm) {
      return
    }

    navigate('/dossier/waardemeters')
  }, [navigate])

  const onSubmit = (data: Record<string, string>) => {
    if (valueMeterIsPending) {
      return
    }

    if (!policy?.policyNumber || !questions) {
      alert('Kan waardemeter niet indienen, ververs de pagina en probeer het opnieuw.')

      return
    }

    const formSubmissionPayload = buildFormSubmissionPayload(data, questions)
    const result = Object.fromEntries(
      Object.keys(formSubmissionPayload).map(key => [key, formSubmissionPayload[key]])
    )

    postContentsInsuranceValue(
      policy?.policyNumber,
      result
    )
      .then(() => {
        showConfirmPostValueMeter()
        navigate('/dossier/waardemeters')
      })
      .catch(e => {
        alert(e)
      })
  }

  return (
    <div className='w-full rounded-lg bg-surface-page p-8'>
      <div className='w-full lg:w-3/4'>
        <p>
          Je garantie tegen onderverzekering verloopt op{' '}

          {expireDate &&
            format(new Date(expireDate), 'dd-MM-yyyy')}
          .

          <strong>
            {' '}
            Verloopt je garantie tegen onderverzekering over drie maanden of
            eerder?
          </strong>{' '}
          Vul onderstaande inboedelwaardemeter dan naar waarheid in. Op basis
          van de ingevulde gegevens zal je verzekeringsadviseur de garantie
          tegen onderverzekering verlengen of contact opnemen.
        </p>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='my-4'>
              {questions && (
                <ValueMeterFormQuestions
                  questions={questions}
                  setValue={form.setValue}
                  watch={form.watch}
                  control={form.control}
                  disabled={valueMeterIsPending}
                />
              )}

              {
                valueMeterIsPending && (
                  <p className='mb-4 text-text-muted'>
                    Er is al een wijzigingsverzoek op deze polis. Neem contact op met je adviseur voor het verwerken van je waardemeter.
                  </p>
                )
              }

              <Button
                type='submit'
                className='primary-btn w-full'
                disabled={!isValid || valueMeterIsPending}
                isLoading={valueMeterIsLoading}
              >
                {valueMeterIsPending ? 'Waardemeter in behandeling' : 'Waardemeter indienen'}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default HouseHoldEffectsForm
